import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../components/AuthContext";
import { terminalAPI } from "../../services/api";
import POSProductList from "./POSProductList";
import POSCart from "./POSCart";
import POSReaderStatus from "./POSReaderStatus";
import POSPayment from "./POSPayment";
import POSReaderRegistration from "./POSReaderRegistration";
import POSProductSync from "./POSProductSync";
import POSCustomerLookup from "./POSCustomerLookup";
import POSGiftCardPayment from "./POSGiftCardPayment";
import StaffLayout from "../staff/StaffLayout";
import {
  POSProvider,
  usePOSCart,
  usePOSTerminal,
  usePOSPayment,
} from "../../context/POSContext";
import "./POSMain.css";

// Wrapper component that provides the POS context
const POSMain = () => {
  return (
    <POSProvider>
      <POSMainContent />
    </POSProvider>
  );
};

// Main component that uses the POS context
const POSMainContent = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [isReaderRegistrationOpen, setIsReaderRegistrationOpen] =
    useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isCustomerLookupOpen, setIsCustomerLookupOpen] = useState(false);
  const [isGiftCardPaymentOpen, setIsGiftCardPaymentOpen] = useState(false);
  const terminalDisplayTimerRef = useRef(null);

  // Use the context hooks
  const {
    items: cartItems,
    totals: cartTotals,
    customer: selectedCustomer,
    appliedDiscount,
    updateQuantity,
    removeItem,
    clearCart,
    updateItems,
    addItem,
    setCustomer,
    clearCustomer,
    applyDiscount,
    removeDiscount,
  } = usePOSCart();

  const {
    readers,
    selectedReader,
    locations,
    selectedLocation,
    error: terminalError,
    setReaders,
    selectReader,
    setLocations,
    selectLocation,
    clearError: clearTerminalError,
    displayCart,
    clearTerminalDisplay,
  } = usePOSTerminal();

  const {
    step: paymentStep,
    paymentData,
    error: paymentError,
    lastOrderCompleted,
    processPayment,
    resetPayment,
    setPaymentSuccess,
    setPaymentFailure,
  } = usePOSPayment();

  // Combine errors from different sources
  const error = terminalError?.message || paymentError;

  // Check if user is authorized
  useEffect(() => {
    if (!token) {
      navigate("/login", { state: { from: "/pos" } });
      return;
    }

    // Check if user has staff or admin role
    if (!user || !(user.role === "staff" || user.role === "admin")) {
      navigate("/");
      return;
    }

    // Load initial data
    fetchLocations();
    fetchProducts();
  }, [token, user, navigate]);

  // Add event listener for gift card payment button
  useEffect(() => {
    const handleOpenGiftCardPayment = () => {
      setIsGiftCardPaymentOpen(true);
    };

    window.addEventListener("openGiftCardPayment", handleOpenGiftCardPayment);

    return () => {
      window.removeEventListener(
        "openGiftCardPayment",
        handleOpenGiftCardPayment
      );
    };
  }, []);

  // Fetch locations
  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await terminalAPI.listLocations();
      setLocations(response.data.locations);

      // If there's only one location, select it automatically
      if (response.data.locations.length === 1) {
        selectLocation(response.data.locations[0]);
        fetchReaders(response.data.locations[0].id);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // Fetch readers for a specific location
  const fetchReaders = async (locationId) => {
    try {
      setLoading(true);

      // First try to find already registered readers
      try {
        const registeredResponse = await terminalAPI.findRegisteredReaders(
          locationId
        );
        if (
          registeredResponse.data.readers &&
          registeredResponse.data.readers.length > 0
        ) {
          setReaders(registeredResponse.data.readers);

          // If there's only one reader, select it automatically
          if (registeredResponse.data.readers.length === 1) {
            selectReader(registeredResponse.data.readers[0]);
          }

          setLoading(false);
          return; // Exit early if we found registered readers
        }
      } catch (registeredError) {
        // Continue to fallback method if finding registered readers fails
      }

      // Fallback to listing all readers if no registered readers found
      const response = await terminalAPI.listReaders(locationId);
      setReaders(response.data.readers);

      // If there's only one reader, select it automatically
      if (response.data.readers.length === 1) {
        selectReader(response.data.readers[0]);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // Fetch products for POS
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await terminalAPI.getProductsForPOS();
      // Fix: Use response.data.products instead of response.data
      setProducts(response.data.products || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // Set products to empty array on error to prevent mapping issues
      setProducts([]);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    setCustomer(customer);
    setIsCustomerLookupOpen(false);
  };

  // Handle payment completion
  const handlePaymentComplete = async (status) => {
    // Only clear the cart if the payment was successful
    if (status === "success") {
      // Then clear the cart and customer
      clearCart();
      clearCustomer();
      setPaymentSuccess();
    } else if (status === "failed") {
      // For failed payments, reset payment state
      resetPayment();
    } else {
      // For any other status (e.g., canceled), just reset payment state
      resetPayment();
    }

    // Always clear discount after payment attempt, regardless of status
    removeDiscount();
  };

  // Get unique categories from products
  const categories = [
    "all",
    ...new Set(products.map((product) => product.category)),
  ];

  // Filter products by search term and category
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.sku &&
        product.sku.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory =
      categoryFilter === "all" || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading && !products.length) {
    return <div className="pos-loading">Loading POS system...</div>;
  }

  return (
    <StaffLayout>
      <div className="pos-container">
        <div className="pos-header">
          <h1>Point of Sale</h1>
          <div className="pos-location-selector">
            {locations.length > 1 && (
              <select
                value={selectedLocation ? selectedLocation.id : ""}
                onChange={(e) => {
                  const location = locations.find(
                    (loc) => loc.id === e.target.value
                  );
                  if (location) selectLocation(location);
                }}
              >
                <option value="" disabled>
                  Select Location
                </option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.display_name}
                  </option>
                ))}
              </select>
            )}

            {selectedLocation && (
              <>
                {readers.length > 0 && (
                  <select
                    value={selectedReader ? selectedReader.id : ""}
                    onChange={(e) => {
                      const reader = readers.find(
                        (r) => r.id === e.target.value
                      );
                      if (reader) selectReader(reader);
                    }}
                  >
                    <option value="" disabled>
                      Select Reader
                    </option>
                    {readers.map((reader) => (
                      <option key={reader.id} value={reader.id}>
                        {reader.label}
                      </option>
                    ))}
                  </select>
                )}
                <div className="pos-header-buttons">
                  <button
                    className="pos-sync-products-btn"
                    onClick={() => {
                      // Create a reference to the POSProductSync component
                      const syncComponent = document.getElementById(
                        "pos-product-sync-component"
                      );
                      if (syncComponent) {
                        // Trigger the sync button click
                        const syncButton =
                          syncComponent.querySelector(".pos-sync-button");
                        if (syncButton) syncButton.click();
                      }
                    }}
                  >
                    <i className="fas fa-sync-alt"></i> Product Sync
                  </button>
                  <button
                    className="pos-register-reader-btn"
                    onClick={() => setIsReaderRegistrationOpen(true)}
                  >
                    <i className="fas fa-plus"></i> Register New Reader
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {error && <div className="pos-error">{error}</div>}

        <div className="pos-main-content">
          <div className="pos-left-panel">
            <div className="pos-search-filter">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pos-search-input"
              />
              <div className="pos-category-filter">
                {categories.map((category) => (
                  <button
                    key={category}
                    className={`pos-category-btn ${
                      categoryFilter === category ? "active" : ""
                    }`}
                    onClick={() => setCategoryFilter(category)}
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <POSProductList products={filteredProducts} addToCart={addItem} />
          </div>

          <div className="pos-right-panel">
            <POSReaderStatus />

            {/* Hidden POSProductSync component that will be triggered from the header button */}
            <div style={{ display: "none" }}>
              <POSProductSync
                id="pos-product-sync-component"
                onSyncComplete={(result) => {
                  setLastSyncTime(result.syncedAt);
                  // Refresh products after sync
                  fetchProducts();
                }}
              />
            </div>

            <POSCart
              disabled={!selectedReader || paymentStep === "processing"}
              onCustomerLookup={() => setIsCustomerLookupOpen(true)}
            />
          </div>
        </div>

        {paymentStep && <POSPayment onComplete={handlePaymentComplete} />}

        <POSReaderRegistration
          isOpen={isReaderRegistrationOpen}
          onClose={() => setIsReaderRegistrationOpen(false)}
          onSuccess={(reader) => {
            // Add the newly registered reader to the readers list
            setReaders((prevReaders) => [...prevReaders, reader]);
            // Select the newly registered reader
            selectReader(reader);
            // Close the modal
            setIsReaderRegistrationOpen(false);
          }}
          locationId={selectedLocation ? selectedLocation.id : null}
        />

        {isCustomerLookupOpen && (
          <POSCustomerLookup
            onCustomerSelect={handleCustomerSelect}
            onClose={() => setIsCustomerLookupOpen(false)}
          />
        )}

        {isGiftCardPaymentOpen && (
          <POSGiftCardPayment
            onClose={() => setIsGiftCardPaymentOpen(false)}
            onSuccess={(giftCardData) => {
              // GIFTCARD-FLOW-002 — Path A success handoff.
              //
              // The modal completes its own SUCCESS terminal sequence
              // (success copy + auto-print + 1.5s auto-dismiss) and
              // then invokes this callback with the final result. Our
              // job here is the cart-side handoff:
              //
              //   - Close the modal (modal already initiated close
              //     via its completeSuccessSequence; this is the
              //     belt-and-suspenders state flip so parent and
              //     child agree).
              //   - On FULL-coverage (Path A, Invariant 5): clear the
              //     cart, clear the customer, and mark the payment
              //     state as success so POS returns to idle.
              //   - On partial-coverage (Path B): do NOT clear. The
              //     cart must stay as the source of truth for "what
              //     does this customer still owe." GIFTCARD-FLOW-003
              //     owns the Path B terminal sequence; this branch
              //     intentionally preserves the deployed behavior of
              //     leaving the cart populated.
              if (!giftCardData) {
                setIsGiftCardPaymentOpen(false);
                return;
              }
              setIsGiftCardPaymentOpen(false);
              if (giftCardData.isFullCoverage) {
                clearCart();
                clearCustomer();
                setPaymentSuccess();
              }
            }}
          />
        )}
      </div>
    </StaffLayout>
  );
};

export default POSMain;
